import { ReactSVG } from "react-svg"
import classNames from "classnames"
import { useRouter } from "next/router"
import dynamic from "next/dynamic"
import { parseISO, compareAsc } from "date-fns"

// ---------------------------------------------------------

import Layout from "../../layout"
import { getLabelIcon, getActivityType, getDateTime } from "@lib/card"
import RenderBlocks from "@lib/renderBlocks"
import { getCurrentDate } from "../../lib/utils"

// ---------------------------------------------------------

import Button from "@components/button"
import Content from "@components/content"
import Image from "@components/image"
import MarketoForm from "@components/marketoForm"

const Speakers = dynamic(() => import("@components/speakers"), { ssr: false })
const TagList = dynamic(() => import("@components/tag-list"), { ssr: false })
const Video = dynamic(() => import("@components/video"), { ssr: false })
const RecommendedReading = dynamic(() => import("@components/recommended-reading"), { ssr: false })

// ---------------------------------------------------------

import {
  button_container,
  column_left,
  column_right,
  column_right_content,
  content_container,
  default_template,
  featured_image,
  light_icon,
  post_header,
  post_header_icon,
  post_header_label,
  post_header_details,
  post_header_details_icon,
  tags_container,
  form_first,
  header
} from "./styles.module.scss"

// ---------------------------------------------------------

const DefaultResourceTemplate = ({ data, recommended, preview }) => {
  const router = useRouter()
  const {
    excerpt,
    isPrivate,
    featuredImage,
    form,
    media,
    modulesCollection,
    pageContent,
    resourceType,
    startDate,
    speakersCollection,
    tagCollection,
    title,
    ungatedDownload,
    formFirstOnMobile
  } = data

  // ---------------------------------------------------------

  const meta = {
    title: title,
    description: excerpt,
    campaign_topics: getActivityType(resourceType),
    robots: isPrivate ? "noindex" : "index"
  }

  let isGated = false
  if (data?.form) {
    data.form["slug"] = data.slug
    data.form["resourceType"] = data.resourceType
    isGated = true
  }

  const sortedTags = tagCollection?.tags.map((tag) => tag.name)

  /**
   *
   * @returns Copy, icons, and date/time details in the hero of the ping pong.
   */
  const getHeader = () => {
    const webinarHasEnded =
      resourceType === "Webinar" && compareAsc(getCurrentDate(), parseISO(startDate)) >= 0

    const transformedLabel =
      resourceType === "Research and Reports" ? "Research & Reports" : resourceType

    return (
      <header className={post_header}>
        <span className={post_header_label}>
          <ReactSVG
            className={classNames(post_header_icon, light_icon)}
            src={`/icons/${getLabelIcon(resourceType)}.svg`}
          />
          {resourceType && webinarHasEnded ? `On-demand ${resourceType}` : transformedLabel}
        </span>
        <h1>{title}</h1>
        {startDate && !webinarHasEnded && (
          <div className={post_header_details}>
            <ReactSVG className={post_header_details_icon} src={`/icons/calendar.svg`} />
            {getDateTime(startDate, undefined, true)}
          </div>
        )}
      </header>
    )
  }

  /**
   * @returns Speaker component if there are speakers.
   */
  const getSpeakers = () => {
    return <Speakers speakersCollection={speakersCollection} />
  }

  /**
   * @returns Featured video or video for the resource, if it exists.
   */
  const getMedia = () => {
    if (media?.videoUrl) return <Video videoUrl={media.videoUrl} />
    return (
      <Image
        className={featured_image}
        height={featuredImage.height}
        width={featuredImage.width}
        src={featuredImage.url}
        alt={featuredImage.description}
        sizes="100vw"
      />
    )
  }

  return (
    <Layout
      preview={preview}
      pageTitle={title}
      featuredImage={featuredImage}
      customMeta={meta}
      navSettings={{
        hideNavLeft: false || router.query._hn,
        hideNavRight: false || router.query._hn
      }}
    >
      {formFirstOnMobile && <div className={header}>{getHeader()}</div>}
      <div className={classNames(default_template, formFirstOnMobile ? form_first : "")}>
        <div className={column_left}>
          {!formFirstOnMobile && getHeader()}
          {isGated && getMedia()}
          <Content className={content_container} content={pageContent} />
          {speakersCollection?.items?.length > 0 && getSpeakers()}
          {tagCollection?.tags?.length > 0 && (
            <div className={tags_container}>
              <p>Tags:</p>
              <TagList tags={sortedTags} path="/resources" theme="dark" />
            </div>
          )}
        </div>

        <div className={column_right}>
          {!isGated && getMedia()}
          {(ungatedDownload || form) && (
            <div className={column_right_content}>
              {ungatedDownload && (
                <div className={button_container}>
                  <h3>View the {resourceType}</h3>
                  <Button theme="prime-teal" url={ungatedDownload.url}>
                    View now
                  </Button>
                </div>
              )}
              {form && <MarketoForm {...form} />}
            </div>
          )}
        </div>
      </div>

      {/* The rest of the modules */}
      {modulesCollection?.items && RenderBlocks(modulesCollection.items)}
      <RecommendedReading recommended={recommended} />
    </Layout>
  )
}

export default DefaultResourceTemplate
